import { differenceInBusinessDays, format } from "date-fns";
import { fr } from "date-fns/locale";
import { ArrowRight, X } from "lucide-react";

import { UPCLAIM_ROLES } from "@upclaim/sdk/src/admin-roles";

import { Can } from "@/components/can";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { BadgeLegalStep } from "@/features/search/components/badge-legal-step";
import { cn } from "@/lib/utils";

import type { ClaimWithRelations } from "../../api/use-claim";
import { useRemoveLastClaimEvent } from "../../api/use-claim-mutations";
import { getAdversaryRefusalReasonById } from "../../lib/adversary-refusal-reasons";
import { claimLegalStepById } from "../../lib/legal-steps";
import { AbandonTypeBadge } from "../abandon-type-badge";
import { EligibilityInsight } from "../ai-insights/eligibility-insight";
import { ClaimNoteElement } from "./claim-note-element";
import type { TimelineEvent } from "./timeline-utils";

interface ClaimTimelineElementProps {
  claim: ClaimWithRelations;
  event: TimelineEvent;
  previousEvent?: TimelineEvent;
  index: number;
  timelineLength: number;
}

/** Port of timeline/ClaimTimelineElement.vue. */
export function ClaimTimelineElement({
  claim,
  event,
  previousEvent,
  index,
  timelineLength,
}: ClaimTimelineElementProps) {
  const removeLastClaimEvent = useRemoveLastClaimEvent(claim.id);

  const isLast = index + 1 === timelineLength;
  const eventDay = format(new Date(event.eventDate), "dd MMMM yyyy", { locale: fr });
  // The time lives in the tooltip rather than the row: a day-resolution timeline cannot show an
  // ordering error between two events on the same date — which is how TECH-892 stayed invisible —
  // but putting it on every row costs a second line everywhere to answer a rare question.
  const eventDayTime = format(new Date(event.eventDate), "dd MMMM yyyy 'à' HH:mm", { locale: fr });
  const abandonType = claim.legalSpecification.abandonType;

  const eventsDiffInDays = previousEvent
    ? differenceInBusinessDays(new Date(event.eventDate), new Date(previousEvent.eventDate))
    : undefined;

  const stepChanged =
    previousEvent && previousEvent.eventType.claimLegalStepId !== event.eventType.claimLegalStepId;

  const refusalReasonTypeId = event.legalHistoryPosition?.legalHistoryAirContext?.refusalReasonTypeId;
  const refusalReason = refusalReasonTypeId ? getAdversaryRefusalReasonById(refusalReasonTypeId) : null;
  const RefusalReasonIcon = refusalReason?.icon;

  return (
    <li className={cn("ml-6 flex flex-wrap justify-between", isLast && index ? "pb-10" : "py-4")}>
      <span className="absolute -left-3 flex h-6 w-6 items-center justify-center rounded-full bg-blue-100 ring-8 ring-white">
        {event.admin?.profilePictureUrl ? (
          <img className="rounded-full shadow-lg" src={event.admin.profilePictureUrl} alt="" />
        ) : (
          <span className="inline-block h-6 w-6 overflow-hidden rounded-full bg-gray-100">
            <svg
              className="h-full w-full text-gray-300"
              fill="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path d="M24 20.993V24H0v-2.996A14.977 14.977 0 0112.004 15c4.904 0 9.26 2.354 11.996 5.993zM16.002 8.999a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
          </span>
        )}
      </span>

      <div>
        <div className="flex">
          {stepChanged && previousEvent ? (
            <>
              <BadgeLegalStep name={claimLegalStepById(previousEvent.eventType.claimLegalStepId)} />
              <ArrowRight className="mx-2 h-5 w-5 text-gray-300" />
            </>
          ) : null}
          <span className="mr-4">
            <BadgeLegalStep name={claimLegalStepById(event.eventType.claimLegalStepId)} />
          </span>
        </div>

        <div className="flex py-2">
          <h3 className="text-base font-semibold text-gray-900">{event.eventType.displayName}</h3>
          {/*
            Always tooltipped, even without a previous event to diff against: the tooltip is the
            only place the event's time is shown, so the oldest event needs one too.
          */}
          <Tooltip>
            <TooltipTrigger
              render={
                <time className="shrink-0 px-2 pt-1.5 text-sm font-normal leading-none text-gray-500" />
              }
            >
              {eventDay}
            </TooltipTrigger>
            {/*
              Above, and click-through: the popup used to open to the right of the date — straight
              over the "supprimer le dernier évènement" cross — and swallow the click on it.
            */}
            <TooltipContent className="pointer-events-none" side="top" align="start">
              <span className="block">{eventDayTime}</span>
              {eventsDiffInDays !== undefined ? (
                <span className="block">{eventsDiffInDays} jours ouvrés après l'évènement précédent</span>
              ) : null}
            </TooltipContent>
          </Tooltip>
          {index === 0 && !isLast ? (
            <Can permissions={UPCLAIM_ROLES}>
              <button
                type="button"
                onClick={() => removeLastClaimEvent.mutate()}
                className="cursor-pointer pt-1 text-sm leading-6"
                aria-label="Supprimer le dernier évènement"
              >
                <X className="h-5 w-5 text-gray-400" />
              </button>
            </Can>
          ) : null}
        </div>

        {event.eventType.displayName.includes("Abandon") ? (
          <div className="flex items-center px-6 pb-2 py-1">
            <AbandonTypeBadge abandonType={abandonType} />
          </div>
        ) : null}

        {event.internalNote ? (
          <Can permissions={[...UPCLAIM_ROLES, "LAWYER"]}>
            <blockquote className="prose gray-700 mb-3 mt-2 whitespace-pre-line text-base font-medium">
              « {event.internalNote} »
            </blockquote>
          </Can>
        ) : null}

        {event.eventType.description ? (
          <p className="prose mb-2 whitespace-pre-line text-base font-normal text-gray-500">
            {event.eventType.description}
          </p>
        ) : null}

        {refusalReason && RefusalReasonIcon ? (
          <Can permissions={[...UPCLAIM_ROLES, "LAWYER"]}>
            <div className="my-4 space-y-3 pb-4">
              <div className="mx-auto flex w-3/4 items-center rounded-lg border border-dashed bg-stone-50 px-6 pb-2">
                <div className="py-1">
                  <div className="my-2 text-xs font-medium text-zinc-500">Raison du refus</div>
                  <div className="inline-flex items-center">
                    <RefusalReasonIcon
                      className={cn(
                        "h-5 w-5 flex-shrink-0",
                        refusalReason.id === 3 ? "text-red-600" : "text-orange-600",
                      )}
                    />
                    <span className="ml-2 text-sm font-semibold text-gray-700">
                      {refusalReason.displayName}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </Can>
        ) : null}

        {event.admin?.fullName ? (
          <p className="text-xs text-gray-700">Ajouté par : {event.admin.fullName}</p>
        ) : null}
      </div>

      {event.messageLink ? (
        <Can permissions={["LAWYER", ...UPCLAIM_ROLES]}>
          <div className="block flex-shrink-0">
            <a
              href={event.messageLink}
              className="relative my-2 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Voir le message
            </a>
          </div>
        </Can>
      ) : null}

      {event.claimNotes.length > 0 ? (
        <div className="ml-8 mt-4 w-full space-y-2 border-l-2 border-gray-200 pl-4">
          <div className="mb-2 text-xs font-medium text-gray-500">
            Notes associées ({event.claimNotes.length})
          </div>
          <ul>
            {event.claimNotes.map((note) => (
              <ClaimNoteElement key={note.id} claimId={claim.id} note={note} />
            ))}
          </ul>
        </div>
      ) : null}

      {isLast ? (
        <Can permissions={[...UPCLAIM_ROLES, "LAWYER"]}>
          <div className="mt-4 w-full">
            <EligibilityInsight claimId={claim.id} />
          </div>
        </Can>
      ) : null}
    </li>
  );
}
